import { inferenceHttp } from '@/lib/api/http-client';

/**
 * GET a binary PDF from `path` (an inference-api path under `/api/v1`) and return
 * it as a validated Blob.
 *
 * Uses the raw axios instance (the generated services aren't built for binary
 * endpoints) with `responseType: 'blob'`. The client's configured `baseURL` /
 * `withCredentials` do NOT apply to direct `instance` calls — a relative URL
 * resolves against the SPA origin instead (Vite answers with its cached
 * `index.html`, seen as a 304) — so resolve the base from the client's config
 * and send the session cookie explicitly, like `downloadBriefPdf`.
 */
const getPdfBlob = async (
  path: string,
  { label, params }: { label: string; params?: Record<string, string> },
): Promise<Blob> => {
  const base = inferenceHttp.getConfig().baseURL ?? '';
  const response = await inferenceHttp.instance.get<Blob>(`${base}/api/v1/${path}`, {
    params,
    responseType: 'blob',
    withCredentials: true,
  });

  // Reject a non-PDF body (HTML error page or SPA shell) instead of saving a broken `.pdf`.
  const blob = response.data;
  const contentType = blob.type || String(response.headers['content-type'] ?? '');
  if (!contentType.includes('application/pdf')) {
    const preview = await blob
      .slice(0, 200)
      .text()
      .catch(() => '');
    throw new Error(
      `${label} PDF download returned a non-PDF response (content-type: ` +
        `"${contentType || 'unknown'}"). First bytes: ${preview.slice(0, 120)}`,
    );
  }

  return blob;
};

/**
 * Download a server-rendered PDF blob from a per-resource `GET /<resource>/:id/pdf`
 * endpoint (persona, focus-group, content-test — see docs → Common Features → PDF Export).
 */
export const downloadPdfBlob = (
  resourcePath: string,
  id: string,
  { label, params }: { label: string; params?: Record<string, string> },
): Promise<Blob> => getPdfBlob(`${resourcePath}/${id}/pdf`, { label, params });

/**
 * Download an artifact as a server-rendered PDF via the generic v2 export
 * endpoint (`GET /artifacts/:artifactId/exports/pdf`), rendered in headless
 * Chromium. `documentType` selects the print page;
 * `itemIndex` picks one item from a collection-backed artifact.
 */
export const downloadArtifactPdfV2 = (
  artifactId: string,
  {
    label,
    documentType,
    workspaceId,
    itemIndex,
  }: { label: string; documentType: string; workspaceId: string; itemIndex?: number },
): Promise<Blob> =>
  getPdfBlob(`artifacts/${artifactId}/exports/pdf`, {
    label,
    params: {
      documentType,
      workspaceId,
      ...(itemIndex != null && { itemIndex: String(itemIndex) }),
    },
  });
